import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type PaginatedResponse } from '@/lib/api-client';
import type { ExpenseKind, ReportFigures } from '@/pages/affiliate/reports-api';

export const PORTAL_PATH = '/api/partner/partner-portal';
export const PARTNER_BANKS_PATH = '/api/partner/banks';
export const PARTNER_SIGNUP_PATH = '/api/partner/partner-signup';

/** Every portal query lives under this key; the auth store drops it on sign-in and sign-out. */
export const PORTAL_QUERY_ROOT = ['partner-portal'] as const;

/** Integer minor units as a decimal string; format with `lib/money`, never `Number()`. */
export type MinorString = string;
/** Bangkok calendar day `YYYY-MM-DD`. */
export type DayKey = string;

export type PortalModel = 'affiliate' | 'master_agent';
export type PortalPartnerStatus = 'pending' | 'active' | 'blocked' | 'cancelled';

export interface PartnerMe {
  id: string;
  username: string;
  code: string | null;
  model: PortalModel;
  isAgent: boolean;
  status: PortalPartnerStatus;
  sharePercentBp: number | null;
  clearingDay: number | null;
  paymentPeriod: 'monthly' | number;
  upline: { username: string; code: string | null } | null;
  groupName: string | null;
  bankCode: string | null;
  bankAccountNo: string | null;
  bankAccountName: string | null;
  email: string | null;
  contactChannel: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  currency: string;
  canInvitePartners: boolean;
  canAddMembers: boolean;
  canAddAgents: boolean;
}

export interface PartnerLinks {
  refCode: string;
  memberLinks: { host: string; url: string }[];
}

/** Members: active = bet in the window. Downline: active = partner status `active`, anything else inactive. */
export type ListStatusFilter = 'all' | 'active' | 'inactive';

/** The members and downline lists take the same filters; `from`/`to` bound the register date. */
export interface PortalListQuery {
  from?: DayKey;
  to?: DayKey;
  username?: string;
  status?: ListStatusFilter;
  page?: number;
  pageSize?: number;
}

/** Rows behind each status tab, after the register-date and username filters. */
export type StatusCounts = Record<ListStatusFilter, number>;

export interface PortalListPage<Row> extends PaginatedResponse<Row> {
  counts: StatusCounts;
}

export interface PortalMemberRow {
  memberId: string;
  username: string;
  registeredAt: string;
  lastLoginAt: string | null;
  turnover: MinorString;
  active: boolean;
}

export interface AddMemberBody {
  username: string;
  password: string;
  bankCode: string;
  bankAccountNo: string;
  bankAccountName: string;
}

export interface PortalDownlineRow {
  id: string;
  username: string;
  code: string | null;
  status: PortalPartnerStatus;
  sharePercentBp: number | null;
  createdAt: string;
  lastLoginAt: string | null;
  membersCount: number;
}

export interface PortalDownline extends PortalListPage<PortalDownlineRow> {
  kind: 'subAffiliates' | 'agents';
}

/** The brand's support channel; `lineId` null when the brand set none. */
export interface PartnerContact {
  lineId: string | null;
}

export interface AddAgentBody {
  username: string;
  password: string;
  sharePercentBp: number;
  bankCode: string;
  bankAccountNo: string;
  bankAccountName: string;
  email?: string;
  contactChannel?: string;
  note?: string;
}

/** Bangkok days, `to` inclusive; the server refuses a reversed or over-long range with PART-427. */
export interface DayRange {
  from: DayKey;
  to: DayKey;
}

export interface ActivityRow {
  day: DayKey;
  register: number;
  ftdCount: number;
  ftdAmount: MinorString;
  active: number;
  deposit: MinorString;
  withdraw: MinorString;
  turnover: MinorString;
  /** Player view: negative means the players lost. */
  winloss: MinorString;
  expenses: Record<'total' | ExpenseKind, MinorString>;
}
export type ActivityTotals = Omit<ActivityRow, 'day'>;

/** The count of an activity row a drill lists the members behind. */
export type ActivityMemberKind = 'register' | 'ftd' | 'active';

export interface ActivityMemberMoney {
  ftdAmount: MinorString | null;
  deposit: MinorString;
  withdraw: MinorString;
  turnover: MinorString;
  /** Player view: negative means the players lost. */
  winloss: { total: MinorString; default: MinorString; boxing: MinorString };
  expenses: Record<'total' | ExpenseKind, MinorString>;
}

export interface ActivityMemberRow extends ActivityMemberMoney {
  memberId: string;
  username: string | null;
  registeredAt: string;
  lastLoginAt: string | null;
  ftdAt: string | null;
}

export interface ActivityMembers {
  kind: ActivityMemberKind;
  items: ActivityMemberRow[];
  totals: ActivityMemberMoney;
}

/** The admin report's figures of a period, or a sum of periods, without the company's own P/L. */
export type PeriodFigures = Omit<ReportFigures, 'companyPl'>;

/** A partner line's figures as its parent sees them: no share the platform pays that partner. */
export type PeriodLegFigures = Omit<PeriodFigures, 'base' | 'initialShare' | 'shareFromPartners' | 'netShare'>;

export interface PeriodLeg {
  partnerId: string;
  code: string | null;
  /** `'***'` when the child hides its name from reports; null once it left the brand. */
  username: string | null;
  sharePercentBp: number | null;
  /** The part of the child's line that reached this partner, partner view. */
  amount: MinorString;
  /** The child's own figures for the period; null once its row is gone. */
  figures: PeriodLegFigures | null;
}

/**
 * Winloss fields are in player view; share fields in partner view (positive = the partner earns).
 * `outstanding` is the balance the period opened on and `netPayable` that plus `netShare`.
 */
export interface PeriodRow extends PeriodFigures {
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  dueOn: DayKey;
  state: 'open' | 'due' | 'closed';
  sharePercentBp: number | null;
  outstanding: MinorString;
  netPayable: MinorString;
  legs: PeriodLeg[];
  legTotals: PeriodLegFigures;
}

export interface PaymentRow {
  createdAt: string;
  action: 'transfer' | 'receive' | 'carry';
  amount: MinorString;
  balanceAfter: MinorString;
  remark: string | null;
  by: 'admin' | 'system';
}

export interface PeriodsReport {
  cadence: 'monthly' | 'weekly';
  rows: PeriodRow[];
  /** Over the rows shown; counts of distinct members (`membersTotal`, `activeCount`) do not add up. */
  totals: PeriodFigures;
  payments: PaymentRow[];
}

/**
 * `winloss` is player view; `agent` (what reaches the partner's line after the company deduction)
 * and `website` (the company's part) are house view, and the three add up to zero.
 */
export interface PlaySplit {
  turnover: MinorString;
  winloss: MinorString;
  agent: MinorString;
  website: MinorString;
}

/** A phone-number `username` arrives masked (`094xxx1063`). */
export interface MemberWinlossRow extends PlaySplit {
  memberId: string;
  username: string | null;
  deposit: MinorString;
  withdraw: MinorString;
}
export type MemberWinlossTotals = Omit<MemberWinlossRow, 'memberId' | 'username'>;

export interface ProviderWinlossRow extends PlaySplit {
  providerId: string;
  providerName: string;
  logoUrl: string | null;
}
export type ProviderWinlossTotals = PlaySplit;

/** The members behind one provider of the provider win/loss report. */
export interface ProviderMembers extends ReportReply<MemberWinlossRow, MemberWinlossTotals> {
  providerId: string;
  providerName: string;
  logoUrl: string | null;
}

/** One Bangkok day; `*Members` count distinct members, so the totals' are not the sum of the days'. */
export interface CashDayRow {
  day: DayKey;
  deposit: MinorString;
  depositMembers: number;
  withdraw: MinorString;
  withdrawMembers: number;
}
export type CashDayTotals = Omit<CashDayRow, 'day'>;

/** `depositCount` / `withdrawCount` are settled transactions. */
export interface CashMemberRow {
  memberId: string;
  username: string | null;
  depositCount: number;
  deposit: MinorString;
  withdrawCount: number;
  withdraw: MinorString;
}
export type CashMemberTotals = Omit<CashMemberRow, 'memberId' | 'username'>;

export interface ReportReply<Row, Totals> {
  rows: Row[];
  totals: Totals;
}

export interface PartnerBank {
  code: string;
  name: string;
  localName?: string | null;
  country?: string | null;
}

export interface PartnerSignupBody {
  refCode: string;
  username: string;
  password: string;
  bankCode: string;
  bankAccountNo: string;
  bankAccountName: string;
  email?: string;
}

export const portalKeys = {
  me: () => [...PORTAL_QUERY_ROOT, 'me'] as const,
  links: () => [...PORTAL_QUERY_ROOT, 'links'] as const,
  members: (q?: PortalListQuery) => [...PORTAL_QUERY_ROOT, 'members', q ?? {}] as const,
  downline: (q?: PortalListQuery) => [...PORTAL_QUERY_ROOT, 'downline', q ?? {}] as const,
  contact: () => [...PORTAL_QUERY_ROOT, 'contact'] as const,
  report: (name: string, q?: object) => [...PORTAL_QUERY_ROOT, 'reports', name, q ?? {}] as const,
};

/** Drops blank values so an empty filter box sends no parameter at all. */
function params(q: object): Record<string, unknown> {
  return Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

const NO_RETRY_ON_REFUSAL = (failures: number, err: unknown) =>
  failures < 1 && !(err instanceof ApiError && err.status >= 400 && err.status < 500);

/** The signed-in partner. A pending or blocked partner gets a 403 (PART-423 / PART-424), never retried. */
export function usePartnerMe(enabled = true) {
  return useQuery({
    queryKey: portalKeys.me(),
    queryFn: () => api.get<PartnerMe>(`${PORTAL_PATH}/me`),
    retry: NO_RETRY_ON_REFUSAL,
    staleTime: 60_000,
    enabled,
  });
}

export function usePartnerLinks() {
  return useQuery({
    queryKey: portalKeys.links(),
    queryFn: () => api.get<PartnerLinks>(`${PORTAL_PATH}/links`),
    retry: NO_RETRY_ON_REFUSAL,
  });
}

export function usePartnerMembers(q: PortalListQuery) {
  return useQuery({
    queryKey: portalKeys.members(q),
    queryFn: () => api.get<PortalListPage<PortalMemberRow>>(`${PORTAL_PATH}/members`, { params: params(q) }),
    placeholderData: keepPreviousData,
    retry: NO_RETRY_ON_REFUSAL,
  });
}

/** 409 on a taken username carries the member service's own code; 403 PART-425 when this partner may not add members. */
export function useAddPartnerMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddMemberBody) =>
      api.post<{ memberId: string; username: string }>(`${PORTAL_PATH}/members`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...PORTAL_QUERY_ROOT, 'members'] }),
  });
}

export function usePartnerDownline(q: PortalListQuery, enabled = true) {
  return useQuery({
    queryKey: portalKeys.downline(q),
    queryFn: () => api.get<PortalDownline>(`${PORTAL_PATH}/downline`, { params: params(q) }),
    placeholderData: keepPreviousData,
    retry: NO_RETRY_ON_REFUSAL,
    enabled,
  });
}

/** 400 PART-414 when the share is above the master's own, 409 PART-102 on a taken username, 403 PART-426 for a non-master. */
export function useAddPartnerAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddAgentBody) => api.post<{ id: string }>(`${PORTAL_PATH}/agents`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...PORTAL_QUERY_ROOT, 'downline'] }),
  });
}

export function usePartnerContact() {
  return useQuery({
    queryKey: portalKeys.contact(),
    queryFn: () => api.get<PartnerContact>(`${PORTAL_PATH}/contact`),
    retry: NO_RETRY_ON_REFUSAL,
    staleTime: 5 * 60 * 1000,
  });
}

function useReport<T>(name: string, q: object, enabled: boolean) {
  return useQuery({
    queryKey: portalKeys.report(name, q),
    queryFn: () => api.get<T>(`${PORTAL_PATH}/reports/${name}`, { params: params(q) }),
    placeholderData: keepPreviousData,
    retry: NO_RETRY_ON_REFUSAL,
    enabled,
  });
}

export function usePartnerActivityReport(range: DayRange, enabled = true) {
  return useReport<ReportReply<ActivityRow, ActivityTotals>>('activity', range, enabled);
}

/** The members behind one register / ftd / active count over the range; idle while `q` is null. */
export function usePartnerActivityMembers(q: (DayRange & { kind: ActivityMemberKind }) | null) {
  return useReport<ActivityMembers>('activity/members', q ?? {}, q !== null);
}

/** The periods starting inside the range, or the newest ones without one. */
export function usePartnerPeriodsReport(range: DayRange | null, enabled = true) {
  return useReport<PeriodsReport>('periods', range ?? {}, enabled);
}

export function usePartnerMemberWinloss(q: DayRange & { username?: string }, enabled = true) {
  return useReport<ReportReply<MemberWinlossRow, MemberWinlossTotals>>('member-winloss', q, enabled);
}

export function usePartnerProviderWinloss(range: DayRange, enabled = true) {
  return useReport<ReportReply<ProviderWinlossRow, ProviderWinlossTotals>>('provider-winloss', range, enabled);
}

/** The own members who played one provider over the range; idle while `q` is null. */
export function usePartnerProviderMembers(q: (DayRange & { providerId: string }) | null) {
  const name = `provider-winloss/${encodeURIComponent(q?.providerId ?? '')}/members`;
  return useReport<ProviderMembers>(name, q ? { from: q.from, to: q.to } : {}, q !== null);
}

export function usePartnerCashReport(range: DayRange, enabled = true) {
  return useReport<ReportReply<CashDayRow, CashDayTotals>>('cash', range, enabled);
}

/** The own members behind the cash report over the range; idle while `range` is null. */
export function usePartnerCashMembers(range: DayRange | null) {
  return useReport<ReportReply<CashMemberRow, CashMemberTotals>>('cash/members', range ?? {}, range !== null);
}

/** Public bank catalogue on the partner audience; answers without a session, so the signup page uses it too. */
export function usePartnerBanks() {
  return useQuery({
    queryKey: ['partner-banks'],
    queryFn: () => api.get<{ items: PartnerBank[] }>(PARTNER_BANKS_PATH),
    staleTime: 5 * 60 * 1000,
  });
}

/** Public. 404 PART-404 / 400 PART-422 for a code that does not enrol, 409 PART-102 for a taken username. */
export function usePartnerSignup() {
  return useMutation({
    mutationFn: (body: PartnerSignupBody) => api.post<{ status: 'pending' }>(PARTNER_SIGNUP_PATH, body),
  });
}

export function portalErrorCode(err: unknown): string | undefined {
  return err instanceof ApiError ? err.code : undefined;
}
